import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle2,
  Eye,
  Heart,
  MessageCircle,
  Bookmark,
  Share2,
  Sparkles,
  Megaphone,
  Bell,
  PenLine,
  Film,
  Palette,
  FileText,
  CalendarDays,
  ArrowUpRight,
  Calendar,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import RoleDashboard from "./RoleDashboard";

const QuickButton = ({ to, icon: Icon, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="aura-card-flat flex items-center gap-3 px-4 py-3 hover:border-[#D99D26]/40 hover:-translate-y-0.5 transition-all"
  >
    <div className="w-9 h-9 rounded-lg bg-[#0A0A0A] text-[#D99D26] flex items-center justify-center">
      <Icon size={17} />
    </div>
    <span className="text-sm font-medium text-[#0A0A0A]">{label}</span>
  </Link>
);

const StatusPill = ({ severity }) => {
  const map = {
    critical: ["pill-red", "Crítico"],
    warning: ["pill-yellow", "Atención"],
    info: ["pill-blue", "Aviso"],
  };
  const [cls, label] = map[severity] || ["pill-gray", severity];
  return <span className={`pill ${cls}`}>{label}</span>;
};

const KpiCard = ({ label, value, sub, accent, icon: Icon, testid }) => (
  <div className="aura-card p-6 fade-up" data-testid={testid}>
    <div className="flex justify-between items-start">
      <div className="label-overline">{label}</div>
      {Icon && (
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === "yellow" ? "bg-[#D99D26]/15 text-[#8a5e10]" : "bg-[#0A0A0A]/5 text-[#0A0A0A]"}`}
        >
          <Icon size={15} />
        </div>
      )}
    </div>
    <div className="mt-3 font-display text-4xl font-medium tracking-tighter">
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-[#666]">{sub}</div>}
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const endpoint =
      user?.role === "ceo"
        ? "/dashboard/ceo"
        : user?.role === "director_marketing"
        ? "/dashboard/director"
        : "/dashboard/me";
    api
      .get(endpoint)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Layout title="Cargando..." subtitle="Dashboard">
        <div className="flex items-center justify-center h-64">
          <span className="spinner" />
        </div>
      </Layout>
    );
  }

  // Role-specific dashboard for non-CEO/Director
  if (user?.role !== "ceo" && user?.role !== "director_marketing") {
    return <RoleDashboard />;
  }

  const k = data?.kpis || {};
  const isCEO = user?.role === "ceo";

  return (
    <Layout
      title={isCEO ? `Hola, ${user.name}` : "Centro de Mando Creativo"}
      subtitle={isCEO ? "Comando ejecutivo" : "Director General de Marketing"}
      actions={
        <Link to="/aura-ai" data-testid="topbar-aura" className="btn-primary flex items-center gap-2">
          <Sparkles size={15} /> Aura AI
        </Link>
      }
    >
      {/* Quick action buttons */}
      <section className="mb-8">
        <div className="label-overline mb-3">Acciones rápidas</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="quick-actions">
          {isCEO ? (
            <>
              <QuickButton to="/campanas" icon={Megaphone} label="Ver campañas" testid="quick-campanas" />
              <QuickButton to="/avisos" icon={Bell} label="Alertas" testid="quick-alertas" />
              <QuickButton to="/publicaciones" icon={Eye} label="Contenido viral" testid="quick-viral" />
              <QuickButton to="/reuniones" icon={Calendar} label="Reuniones" testid="quick-reuniones" />
              <QuickButton to="/aura-ai" icon={Sparkles} label="Aprendizajes IA" testid="quick-aura" />
              <QuickButton to="/competencia" icon={Eye} label="Competencia" testid="quick-comp" />
            </>
          ) : (
            <>
              <QuickButton to="/campanas?new=1" icon={Megaphone} label="Crear campaña" testid="quick-campana" />
              <QuickButton to="/guiones?new=1" icon={Film} label="Generar guion" testid="quick-guion" />
              <QuickButton to="/copy-studio?new=1" icon={PenLine} label="Generar copy" testid="quick-copy" />
              <QuickButton to="/briefs?new=1" icon={FileText} label="Crear brief" testid="quick-brief" />
              <QuickButton to="/calendario" icon={CalendarDays} label="Calendario" testid="quick-cal" />
              <QuickButton to="/diseno" icon={Palette} label="Diseño" testid="quick-diseno" />
            </>
          )}
        </div>
      </section>

      {/* KPI Grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="kpi-grid">
        <KpiCard label="Campañas activas" value={k.active_campaigns ?? 0} sub={`${k.delayed_campaigns ?? 0} con retraso`} icon={Megaphone} accent="yellow" testid="kpi-campanas" />
        <KpiCard label="Tareas en proceso" value={k.in_progress_tasks ?? 0} sub={`${k.overdue_tasks ?? 0} atrasadas`} icon={Clock} testid="kpi-tasks" />
        <KpiCard label="Diseños pendientes" value={k.designs_pending ?? 0} sub={`${k.scripts_pending ?? 0} guiones`} icon={Palette} testid="kpi-designs" />
        <KpiCard label="Publicados" value={k.published_posts ?? 0} sub={`${k.scheduled_posts ?? 0} programados`} icon={CheckCircle2} accent="yellow" testid="kpi-published" />
      </section>

      {/* Two columns: Campaigns + Alerts */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 aura-card-flat p-6" data-testid="campaigns-progress">
          <div className="flex justify-between items-end mb-5">
            <div>
              <div className="label-overline">Campañas en curso</div>
              <h2 className="font-display text-2xl font-medium mt-1">Progreso global</h2>
            </div>
            <Link to="/campanas" className="text-xs font-medium text-[#8a5e10] flex items-center gap-1 hover:gap-2 transition-all">
              Ver todas <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="space-y-4">
            {(data?.campaigns || []).slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center gap-4">
                <div
                  className="w-1.5 h-12 rounded-full"
                  style={{ background: c.color || "#D99D26" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <div className="font-medium text-[#0A0A0A] truncate">{c.name}</div>
                    <div className="text-xs text-[#666]">{c.progress || 0}%</div>
                  </div>
                  <div className="h-1.5 bg-[#F0F0F0] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${c.progress || 0}%`, background: c.color || "#D99D26" }}
                    />
                  </div>
                  <div className="text-[11px] text-[#666] mt-1">{c.objective}</div>
                </div>
                <span className={`pill ${c.status === "activa" ? "pill-green" : c.status === "planeacion" ? "pill-blue" : "pill-gray"}`}>
                  {c.status}
                </span>
              </div>
            ))}
            {(!data?.campaigns || data.campaigns.length === 0) && (
              <div className="text-center py-8 text-[#666] text-sm">
                Sin campañas. Crea la primera para empezar.
              </div>
            )}
          </div>
        </div>

        <div className="aura-card-flat p-6" data-testid="alerts-panel">
          <div className="flex justify-between items-center mb-5">
            <div className="label-overline flex items-center gap-2">
              <AlertCircle size={12} /> Avisos estratégicos
            </div>
            <Link to="/avisos" className="text-xs font-medium text-[#8a5e10]">Ver todo</Link>
          </div>
          <div className="space-y-3">
            {(data?.alerts || []).slice(0, 6).map((a) => (
              <div key={a.id} className="border-b border-[#F0F0F0] pb-3 last:border-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#0A0A0A]">{a.title}</div>
                    {a.description && (
                      <div className="text-xs text-[#666] mt-0.5">{a.description}</div>
                    )}
                  </div>
                  <StatusPill severity={a.severity} />
                </div>
              </div>
            ))}
            {(!data?.alerts || data.alerts.length === 0) && (
              <div className="text-center py-6 text-[#666] text-sm">Sin avisos activos</div>
            )}
          </div>
        </div>
      </section>

      {/* Top viral content + Creator ranking */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="aura-card-flat p-6" data-testid="top-viral">
          <div className="label-overline mb-1">Contenido</div>
          <h2 className="font-display text-2xl font-medium mb-5">Top viral</h2>
          {(data?.top_viral || []).length === 0 ? (
            <div className="text-center py-10 text-[#666] text-sm">
              Registra publicaciones para ver tu top viral
              <div className="mt-4">
                <Link to="/publicaciones" className="btn-outline inline-block">Registrar publicación</Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {(data?.top_viral || []).slice(0, 5).map((p, i) => (
                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-[#F0F0F0] last:border-0">
                  <div className="font-display text-xl text-[#D99D26] w-6">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{p.title}</div>
                    <div className="text-xs text-[#666]">{p.creator_name} · {p.platform} · {p.format}</div>
                  </div>
                  <div className="flex gap-3 text-xs text-[#666]">
                    <span className="flex items-center gap-1"><Eye size={12} /> {p.views?.toLocaleString() || 0}</span>
                    <span className="flex items-center gap-1"><Heart size={12} /> {p.likes?.toLocaleString() || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="aura-card-flat p-6" data-testid="creator-ranking">
          <div className="label-overline mb-1">Equipo</div>
          <h2 className="font-display text-2xl font-medium mb-5">Ranking creadores</h2>
          {(data?.creator_ranking || []).length === 0 ? (
            <div className="text-center py-10 text-[#666] text-sm">
              El ranking aparecerá cuando registres publicaciones
            </div>
          ) : (
            <div className="space-y-2">
              {(data?.creator_ranking || []).slice(0, 7).map((c, i) => (
                <div key={c.creator} className="flex items-center gap-3 py-2 border-b border-[#F0F0F0] last:border-0">
                  <div className="font-display text-lg text-[#666] w-6">{i + 1}</div>
                  <div className="flex-1 font-medium text-sm">{c.creator}</div>
                  <div className="flex gap-3 text-xs text-[#666]">
                    <span>{c.total_posts} posts</span>
                    <span className="text-[#0A0A0A] font-medium">{(c.total_views || 0).toLocaleString()} vistas</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Aura Learnings + Upcoming meetings */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="aura-card-flat p-6" data-testid="aura-learnings">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="label-overline flex items-center gap-2"><Sparkles size={11} /> Aura AI</div>
              <h2 className="font-display text-xl font-medium mt-1">Aprendizajes pendientes</h2>
            </div>
            <Link to="/aura-ai" className="text-xs font-medium text-[#8a5e10]">Ver centro</Link>
          </div>
          {(data?.pending_learnings || []).length === 0 ? (
            <div className="text-center py-6 text-[#666] text-sm">
              Sin aprendizajes pendientes
            </div>
          ) : (
            <div className="space-y-3">
              {(data?.pending_learnings || []).map((l) => (
                <div key={l.id} className="border-l-2 border-[#D99D26] pl-3 py-1">
                  <div className="text-sm font-medium">{l.title}</div>
                  <div className="text-xs text-[#666] line-clamp-2 mt-0.5">{l.insight}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="aura-card-flat p-6" data-testid="upcoming-meetings">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="label-overline">Calendario</div>
              <h2 className="font-display text-xl font-medium mt-1">Próximas reuniones</h2>
            </div>
            <Link to="/reuniones" className="text-xs font-medium text-[#8a5e10]">Ver todas</Link>
          </div>
          {(data?.upcoming_meetings || []).length === 0 ? (
            <div className="text-center py-6 text-[#666] text-sm">
              Sin reuniones programadas
              <div className="mt-3">
                <Link to="/reuniones" className="btn-outline inline-block text-sm">Crear reunión</Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {(data?.upcoming_meetings || []).map((m) => (
                <div key={m.id} className="border border-[#E5E5E5] rounded-lg p-3 hover:border-[#D99D26]/40 transition-all">
                  <div className="text-sm font-medium">{m.title}</div>
                  <div className="text-xs text-[#666] mt-1">
                    {new Date(m.date).toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {(m.participants || []).slice(0, 4).map((p) => (
                      <span key={p} className="pill pill-gray text-[10px]">{p}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

const PersonalDashboard = ({ data, user }) => {
  return (
    <Layout title={`Hola, ${user.name}`} subtitle="Mi espacio creativo">
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KpiCard label="Mis tareas" value={(data?.tasks || []).length} icon={Clock} testid="kpi-mytasks" />
        <KpiCard label="Mis briefs" value={(data?.briefs || []).length} icon={FileText} testid="kpi-mybriefs" />
        <KpiCard label="Mis publicaciones" value={(data?.posts || []).length} icon={Eye} accent="yellow" testid="kpi-myposts" />
      </section>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="aura-card-flat p-6">
          <h2 className="font-display text-xl font-medium mb-4">Mis tareas</h2>
          <div className="space-y-2">
            {(data?.tasks || []).map((t) => (
              <div key={t.id} className="border-b border-[#F0F0F0] py-2 last:border-0">
                <div className="font-medium text-sm">{t.title}</div>
                <div className="text-xs text-[#666] mt-1">{t.status} · {t.priority}</div>
              </div>
            ))}
            {(!data?.tasks || data.tasks.length === 0) && (
              <div className="text-center py-6 text-[#666] text-sm">Sin tareas asignadas</div>
            )}
          </div>
        </div>
        <div className="aura-card-flat p-6">
          <h2 className="font-display text-xl font-medium mb-4">Mis briefs</h2>
          <div className="space-y-2">
            {(data?.briefs || []).map((b) => (
              <div key={b.id} className="border-b border-[#F0F0F0] py-2 last:border-0">
                <div className="font-medium text-sm">{b.title}</div>
                <div className="text-xs text-[#666] mt-1">{b.type} · {b.status}</div>
              </div>
            ))}
            {(!data?.briefs || data.briefs.length === 0) && (
              <div className="text-center py-6 text-[#666] text-sm">Sin briefs asignados</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
